import { createSlice } from '@reduxjs/toolkit';
import { pushDynamicTab } from '../utils/dynamicTabs';

// 🔹 Define the current version of the slice
const SLICE_VERSION = '1.3';

const getAllTabs = (state) => [...state.defaultTabs, ...state.dynamicTabs];

// Record the tab the user is leaving so a later tab closure can return to it.
const pushTabHistory = (state, tabId) => {
  if (!tabId) return;
  state.tabHistory = state.tabHistory.filter((id) => id !== tabId);
  state.tabHistory.push(tabId);
};

// 🔹 Exported initial state for use in version-based merging
export const initialState = {
  _version: SLICE_VERSION,
  defaultTabs: [
    { id: 'Appointments', name: 'My Appointments' },
  ],
  dynamicTabs: [],
  activeTab: 0,
  // Stack of previously active tab ids, most-recent-last — lets removeTab send the
  // user back to whichever tab/page they were actually viewing before, not tab 0.
  tabHistory: [],

  fetchedWarehouseIds: [],
  warehousesByLocationId: {},

  // 🔹 NEW — Persisted User Selections
  lastSelectedLocation: null,        // { id, locationRef, locationName }
  lastSelectedWarehouse: null,       // { id, locationRef, locationName }
  lastSelectedDate: null,            // YYYY-MM-DD

  // 🔹 NEW — Separate selections for Calendar Settings tab
  lastSettingsLocation: null,        // { id, locationRef, locationName }
  lastSettingsWarehouse: null,       // { id, locationRef, locationName }
};

// 🔹 Create Appointments slice
const AppointmentsSlice = createSlice({
  name: 'Appointments',
  initialState,
  reducers: {
    addTab: (state, action) => {
      let tab = action.payload;

      if (!tab) {
        const dynamicId =
          window.crypto?.randomUUID?.() ||
          `${Date.now()}-${Math.random().toString(36).slice(2, 15)}-${state.dynamicTabs.length}`;

        tab = {
          id: `dyn-${dynamicId}`,
          name: `New Appointment ${state.dynamicTabs.length + 1}`,
          contentType: "newAppointment",
          isDynamic: true,
          createdAt: Date.now(),
        };
      }

      const prevActiveId = getAllTabs(state)[state.activeTab]?.id;

      // ✅ Dedupe: Prevent duplicate tabs
      if (tab?.key) {
        const existingIdx = state.dynamicTabs.findIndex(
          (t) => t.key === tab.key && t.contentType === tab.contentType
        );

        if (existingIdx !== -1) {
          const nextIndex = state.defaultTabs.length + existingIdx;
          if (nextIndex !== state.activeTab) {
            pushTabHistory(state, prevActiveId);
            state.activeTab = nextIndex;
          }
          return;
        }
      }

      const added = pushDynamicTab(state, tab, state.defaultTabs.length);
      if (added) pushTabHistory(state, prevActiveId);
    },

    updateAppointmentTab: (state, action) => {
      const { appointmentId, appointmentData } = action.payload || {};
      if (!appointmentId || !appointmentData) return;

      state.dynamicTabs.forEach((tab) => {
        const matchesId =
          tab?.appointmentData?.appointmentId === appointmentId ||
          tab?.id === `view-appointment-${appointmentId}` ||
          tab?.id === `edit-appointment-${appointmentId}`;
        if (!matchesId) return;
        tab.appointmentData = appointmentData;
      });
    },

    removeTab: (state, action) => {
      const { tabIndex } = action.payload;

      // Prevent deleting default tabs
      if (tabIndex < state.defaultTabs.length) return;

      const tabsBefore = getAllTabs(state);
      const removedId = tabsBefore[tabIndex]?.id;
      const activeId = tabsBefore[state.activeTab]?.id;
      const wasActive = tabIndex === state.activeTab;

      const dynamicIndex = tabIndex - state.defaultTabs.length;
      state.dynamicTabs.splice(dynamicIndex, 1);
      state.tabHistory = state.tabHistory.filter((id) => id !== removedId);

      const tabsAfter = getAllTabs(state);

      if (!wasActive) {
        // The tab the user was viewing wasn't the one closed — stay on it, just
        // recompute its index since removing an earlier tab shifts the array.
        const newIndex = tabsAfter.findIndex((t) => t.id === activeId);
        state.activeTab = newIndex >= 0 ? newIndex : Math.min(state.activeTab, tabsAfter.length - 1);
        return;
      }

      // The active tab itself was closed — return to the last tab/page the user
      // was actually on, walking back through history until one still exists.
      while (state.tabHistory.length) {
        const candidateId = state.tabHistory.pop();
        const idx = tabsAfter.findIndex((t) => t.id === candidateId);
        if (idx >= 0) {
          state.activeTab = idx;
          return;
        }
      }

      // No usable history — fall back to the default Appointments tab.
      state.activeTab = 0;
    },

    setActiveTab: (state, action) => {
      const { index } = action.payload;
      if (index >= 0 && index < state.defaultTabs.length + state.dynamicTabs.length && index !== state.activeTab) {
        pushTabHistory(state, getAllTabs(state)[state.activeTab]?.id);
        state.activeTab = index;
      }
    },

    resetTabs: (state) => {
      state.dynamicTabs = [];
      state.activeTab = 0;
      state.tabHistory = [];
    },

    // ---------- EXISTING STATES ----------
    addFetchedWarehouseId: (state, action) => {
      const id = action.payload;
      if (!state.fetchedWarehouseIds.includes(id)) {
        state.fetchedWarehouseIds.push(id);
      }
    },

    setWarehousesForLocation: (state, action) => {
      const { locationId, warehouses } = action.payload;
      state.warehousesByLocationId[locationId] = warehouses;
    },

    // ---------- NEW REDUCERS FOR PERSISTING SELECTIONS ----------
    setLastSelectedLocation: (state, action) => {
      state.lastSelectedLocation = action.payload;
    },

    setLastSelectedWarehouse: (state, action) => {
      state.lastSelectedWarehouse = action.payload;
    },

    setLastSelectedDate: (state, action) => {
      state.lastSelectedDate = action.payload;
    },

    setLastSettingsLocation: (state, action) => {
      state.lastSettingsLocation = action.payload;
    },

    setLastSettingsWarehouse: (state, action) => {
      state.lastSettingsWarehouse = action.payload;
    },
  },
});

// 🔹 Export actions and reducer
export const {
  addTab,
  updateAppointmentTab,
  removeTab,
  setActiveTab,
  resetTabs,
  addFetchedWarehouseId,
  setWarehousesForLocation,

  // NEW exports
  setLastSelectedLocation,
  setLastSelectedWarehouse,
  setLastSelectedDate,

  setLastSettingsLocation,
  setLastSettingsWarehouse,
} = AppointmentsSlice.actions;

export default AppointmentsSlice.reducer;
